/**
 * Client-Side EMVCo ISO/IEC 18004 TLV PromptPay Generator & Inspector
 */

export function crc16(data: string): string {
  let crc = 0xFFFF;
  for (let i = 0; i < data.length; i++) {
    crc ^= (data.charCodeAt(i) << 8);
    for (let j = 0; j < 8; j++) {
      if ((crc & 0x8000) !== 0) {
        crc = ((crc << 1) ^ 0x1021) & 0xFFFF;
      } else {
        crc = (crc << 1) & 0xFFFF;
      }
    }
  }
  return crc.toString(16).toUpperCase().padStart(4, '0');
}

export function formatTlv(tag: string, value: string): string {
  const len = value.length.toString().padStart(2, '0');
  return `${tag}${len}${value}`;
}

export interface ClientPromptPayOptions {
  merchantCode?: string;
  merchantName?: string;
  billerId?: string;
  promptPayId?: string;
  amount?: number;
  ref1?: string;
  ref2?: string;
  isDynamic?: boolean;
}

export function generatePromptPayPayload(options: ClientPromptPayOptions): string {
  const isDynamic = options.isDynamic !== false && (options.amount !== undefined && options.amount > 0);
  let payload = '';

  // Tag 00: Version
  payload += formatTlv('00', '01');

  // Tag 01: Initiation Method
  payload += formatTlv('01', isDynamic ? '12' : '11');

  const activeMerchantCode = options.merchantCode || 'QV353070BI06881536X';
  const activeMerchantName = options.merchantName || 'GIFFFORYOU.COM';

  // Tag 29 / 30: Merchant Account
  if (options.billerId && options.billerId.trim().length > 0) {
    const biller = options.billerId.replace(/[^0-9]/g, '');
    let tag30Value = '';
    tag30Value += formatTlv('00', 'A000000677010112');
    tag30Value += formatTlv('01', biller);
    // Subtag 02 (REF 1): Merchant Code (รหัสผู้ค้า QV353070BI06881536X ต้องเป็นค่านี้เท่านั้น)
    tag30Value += formatTlv('02', activeMerchantCode);
    // Subtag 03 (REF 2): Order Reference (เลขคำสั่งซื้อ)
    let ref2Val = options.ref2 || '';
    if (!ref2Val && options.ref1 && options.ref1 !== activeMerchantCode && options.ref1 !== 'QV353070BI06881536X') {
      ref2Val = options.ref1;
    }
    if (ref2Val) {
      tag30Value += formatTlv('03', ref2Val.substring(0, 20));
    }
    payload += formatTlv('30', tag30Value);
  } else {
    const rawId = (options.promptPayId || '010753700088205').replace(/[^0-9]/g, '');
    let tag29Value = '';
    tag29Value += formatTlv('00', 'A000000677010111');

    if (rawId.length === 10 && rawId.startsWith('0')) {
      tag29Value += formatTlv('01', '0066' + rawId.substring(1));
    } else if (rawId.length === 13) {
      tag29Value += formatTlv('02', rawId);
    } else if (rawId.length === 15) {
      tag29Value += formatTlv('03', rawId);
    } else {
      tag29Value += formatTlv('01', rawId);
    }
    payload += formatTlv('29', tag29Value);
  }

  // Tag 53: THB
  payload += formatTlv('53', '764');

  // Tag 54: Amount
  if (options.amount !== undefined && options.amount > 0) {
    payload += formatTlv('54', options.amount.toFixed(2));
  }

  // Tag 58: TH
  payload += formatTlv('58', 'TH');

  // Tag 59: Merchant Name
  payload += formatTlv('59', activeMerchantName.substring(0, 25));

  // Tag 60: Merchant City
  payload += formatTlv('60', 'BANGKOK');

  // Tag 62: Additional Data (Store Label & Terminal Label) - ลบออกเมื่อเป็น Bill Payment เพื่อไม่ให้แสดง เลขที่อ้างอิง 3
  if (!options.billerId) {
    let tag62 = '';
    tag62 += formatTlv('03', activeMerchantCode.substring(0, 25));
    if (options.ref1) {
      tag62 += formatTlv('07', options.ref1.substring(0, 25));
    }
    payload += formatTlv('62', tag62);
  }

  // Tag 63: CRC16 Checksum
  payload += '6304';
  const checksum = crc16(payload);
  payload += checksum;

  return payload;
}

export interface ClientTlvItem {
  tag: string;
  length: number;
  value: string;
  name: string;
  description: string;
  children?: ClientTlvItem[];
}

export function parseEmvcoPayload(payload: string): { items: ClientTlvItem[]; isValidChecksum: boolean; calculatedCrc: string; providedCrc: string } {
  const items: ClientTlvItem[] = [];
  let index = 0;
  let providedCrc = '';

  const tagNames: Record<string, { name: string; desc: string }> = {
    '00': { name: 'Payload Format Indicator', desc: 'เวอร์ชันมาตรฐาน EMVCo (01)' },
    '01': { name: 'Point of Initiation Method', desc: '11 = Static QR (แบบคงที่), 12 = Dynamic QR (แบบระบุยอด)' },
    '29': { name: 'Merchant Account (PromptPay)', desc: 'ข้อมูลบัญชีพร้อมเพย์ (AID & PromptPay ID)' },
    '30': { name: 'Merchant Account (Bill Payment)', desc: 'ข้อมูลบิลเลอร์รับชำระเงิน (Biller ID & Ref 1/2)' },
    '52': { name: 'Merchant Category Code', desc: 'รหัสหมวดหมู่ร้านค้า (MCC)' },
    '53': { name: 'Transaction Currency', desc: 'รหัสสกุลเงิน (764 = บาทไทย / THB)' },
    '54': { name: 'Transaction Amount', desc: 'ยอดเงินที่ต้องชำระ (บาท)' },
    '58': { name: 'Country Code', desc: 'รหัสประเทศ (TH = ประเทศไทย)' },
    '59': { name: 'Merchant Name', desc: 'ชื่อร้านค้า/ผู้รับเงิน' },
    '60': { name: 'Merchant City', desc: 'เมือง/จังหวัดของร้านค้า' },
    '62': { name: 'Additional Data Field', desc: 'ข้อมูลอ้างอิงเพิ่มเติม (Reference ID)' },
    '63': { name: 'CRC16-CCITT Checksum', desc: 'รหัสตรวจสอบความถูกต้องของข้อมูล (4 หลัก)' }
  };

  while (index < payload.length) {
    if (index + 4 > payload.length) break;
    const tag = payload.substring(index, index + 2);
    const length = parseInt(payload.substring(index + 2, index + 4), 10);
    const value = payload.substring(index + 4, index + 4 + length);
    index += 4 + length;

    const meta = tagNames[tag] || { name: `Tag ${tag}`, desc: 'EMVCo Proprietary Tag' };
    const item: ClientTlvItem = {
      tag,
      length,
      value,
      name: meta.name,
      description: meta.desc
    };

    if (tag === '63') {
      providedCrc = value;
    }

    if ((tag === '29' || tag === '30' || tag === '62') && value.length >= 4) {
      item.children = [];
      let subIdx = 0;
      while (subIdx < value.length) {
        if (subIdx + 4 > value.length) break;
        const subTag = value.substring(subIdx, subIdx + 2);
        const subLen = parseInt(value.substring(subIdx + 2, subIdx + 4), 10);
        const subVal = value.substring(subIdx + 4, subIdx + 4 + subLen);
        subIdx += 4 + subLen;

        let subName = `Sub-tag ${subTag}`;
        let subDesc = '';
        if (subTag === '00') { subName = 'AID (Application ID)'; subDesc = 'รหัสมาตรฐานระบบพร้อมเพย์ (PromptPay / Bill Payment AID)'; }
        else if (subTag === '01') { subName = 'Biller ID / Mobile ID'; subDesc = 'รหัสบิลเลอร์ 15 หลัก (Tag 30) หรือ เบอร์โทรพร้อมเพย์ (Tag 29)'; }
        else if (subTag === '02') { 
          if (tag === '30') {
            subName = 'Ref 1 (Merchant Code / รหัสผู้ค้า)'; 
            subDesc = 'รหัสผู้ค้าผู้รับเงิน (Merchant Code: QV353070BI06881536X)';
          } else {
            subName = 'Ref 1 (Order Reference)'; 
            subDesc = 'เลขที่อ้างอิงคำสั่งซื้อ 1 (Order ID)';
          }
        }
        else if (subTag === '03') { 
          if (tag === '30') {
            subName = 'Ref 2 (Order Reference / เลขอ้างอิงคำสั่งซื้อ)'; 
            subDesc = 'เลขที่อ้างอิงคำสั่งซื้อ (Order Reference / Customer ID)';
          } else if (tag === '62') {
            subName = 'Store Label (รหัสร้านค้า)';
            subDesc = 'รหัสประจำตัวร้านค้าของผู้รับเงิน (Store / Merchant Identifier)';
          } else {
            subName = 'E-Wallet / Ref 2'; 
            subDesc = 'กระเป๋าเงิน หรือ เลขที่อ้างอิง 2'; 
          }
        }
        else if (subTag === '07') { subName = 'Terminal Label / Reference'; subDesc = 'เลขอ้างอิงคำสั่งซื้อ (Order Reference Label)'; }

        item.children.push({
          tag: subTag,
          length: subLen,
          value: subVal,
          name: subName,
          description: subDesc
        });
      }
    }

    items.push(item);
  }

  const rawBeforeCrc = payload.substring(0, payload.indexOf('6304') + 4);
  const calculatedCrc = crc16(rawBeforeCrc);
  const isValidChecksum = Boolean(providedCrc && calculatedCrc.toUpperCase() === providedCrc.toUpperCase());

  return {
    items,
    isValidChecksum,
    calculatedCrc,
    providedCrc
  };
}
